import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { Route, Routes, useNavigate, useLocation, Navigate } from 'react-router-dom';
import io from 'socket.io-client';
import { jwtDecode } from 'jwt-decode';
import Onboarding from './components/Onboarding';
import AuthGuard from './components/AuthGuard';
import Connexion from './components/Connexion';
import TwoFactor from './components/TwoFactor';
import DiscussionList from './components/DiscussionList';
import { DiscussionsProvider } from "./components/DiscussionsContext";
import VerifyEmail from './components/VerifyEmail';
import MenuComponent from './components/Menu';
import MenuBasComponent from './components/MenuBas';
import './styles/styles.css';
import './styles/index.css';
import './styles/App.css';
import { Buffer } from 'buffer';
import process from 'process/browser';
import usePrompt from './components/usePrompt';
import i18n from './i18n';
import { useTranslation } from 'react-i18next';
import api from './services/api';
import { hasLocalIdentity } from './services/signalManager';
import { getGroupKeyForDiscussion, rewrapGroupKeyForParticipant } from './services/signalGroupCrypto';
import useSessionReset  from './hooks/useSessionReset';



// ── Constantes hors composant ─────────────────────────────────────────────────

const API_URL    = process.env.REACT_APP_API_URL    || 'http://localhost:3001';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';

if (typeof window !== 'undefined') {
  const isCordova = !!window.cordova || 
                    window.location.protocol === 'file:' ||
                    window.location.protocol === 'cdvfile:';

  // Ne rediriger QUE sur le web, jamais dans Cordova
  if (!isCordova) {
    const path   = window.location.pathname;
    const search = window.location.search;

    if (path !== '/' && !window.location.hash) {
      window.location.replace('/#' + path + search);
    }
  }
}


function App() {

const TwoFactorSetup   = lazy(() => import('./components/TwoFactorSetup'));
const Discussion       = lazy(() => import('./components/Discussion'));
const Profile          = lazy(() => import('./components/Profile'));
const UserProfile      = lazy(() => import('./components/UserProfile'));
const UserList   = lazy(() => import('./components/UserList'));
const Whiteboard       = lazy(() => import('./components/Whiteboard'));
const ImportContacts   = lazy(() => import('./components/ImportContacts'));
const CreateContact   = lazy(() => import('./components/CreateContact'));
const Infos   = lazy(() => import('./components/Infos'));
const Parametres       = lazy(() => import('./components/Parametres'));
const RecoverySettings   = lazy(() => import('./components/RecoverySettings'));
const ParametresLangue   = lazy(() => import('./components/Parametres_langue'));
const VideoCall        = lazy(() => import('./components/VideoCall'));
const Dashboard        = lazy(() => import('./components/Dashboard'));
const QuizPage         = lazy(() => import('./components/quiz'));
const PublicGroups     = lazy(() => import('./components/PublicGroups'));

  const [user, setUser]           = useState(null);
  const [userObj, setUserObj]     = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [authState, setAuthState] = useState(null);
  const [socket, setSocket]       = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [callInProgress, setCallInProgress] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [contacts, setContacts]   = useState([]);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  

  const isCordova    = typeof window !== 'undefined' && window.cordova;
  const navigate     = useNavigate();
  const location     = useLocation();
  const socketRef    = useRef(null);
  const refreshTimerRef = useRef(null);
  const prompt       = usePrompt();
  const { t }        = useTranslation();
const PUBLIC_ROUTES = [
  '/verifyemail',
  '/statsElsa@1973',
  '/2fa',
  '/onboarding',
  '/connexion',
];



  const hideMenuRoutes = PUBLIC_ROUTES; // même liste
  const shouldHideMenu = hideMenuRoutes.includes(location.pathname);

  const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];
  const savedLang = localStorage.getItem('i18nextLng') || 'fr';
  const isRTL = RTL_LANGUAGES.includes(savedLang);
  document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
  document.documentElement.setAttribute('lang', savedLang);

  // ── Langue ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const browserLang  = navigator.language.split('-')[0];
    const storedLang   = localStorage.getItem('lang');
    i18n.changeLanguage(storedLang || browserLang || 'fr');
  }, []);

  // ── Thème ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // ── Deep link ──────────────────────────────────────────────────────────────
  useEffect(() => {
    window.handleOpenURL = (url) => {
      const urlObj  = new URL(url.replace("clavardage://", "https://fake/"));
      const message = urlObj.searchParams.get("message");
      if (message) {
        localStorage.setItem('emailVerificationMessage', decodeURIComponent(message));
        window.location.hash = "#/connexion";
      }
    };
  }, []);

  // ── Logout ─────────────────────────────────────────────────────────────────
  const handleLogout = useCallback(() => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('contacts');
  setAuthToken(null);
  socketRef.current?.disconnect();
  socketRef.current = null;
  setUser(null);
  navigate('/onboarding');
}, [navigate]);

  // ── Token helpers ──────────────────────────────────────────────────────────
  const isTokenExpiringSoon = useCallback((token) => {
    try {
      const { exp } = jwtDecode(token);
      return exp - Date.now() / 1000 < 30;
    } catch {
      return true;
    }
  }, []);

  const refreshAccessToken = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) return null;

      const response = await api.post(
        `${API_URL}/api/authRefresh/refresh-token`,
        { refreshToken }
      );
      const { token, refreshToken: newRefreshToken } = response.data || {};
      if (!token) return null;

      localStorage.setItem("token", token);
      if (newRefreshToken) localStorage.setItem("refreshToken", newRefreshToken);
      setAuthToken(token);
      return token;
    } catch (error) {
      console.error("❌ Erreur refresh token :", error);
      return null;
    }
  }, []);

async function getFreshToken() {
  const token = localStorage.getItem('token');
  try {
    const { exp } = jwtDecode(token);
    if (exp * 1000 - Date.now() > 60000) return token;       // encore valable plus d'une minute
  } catch {
    // jeton absent ou illisible : on tente un rafraîchissement
  }
  const refreshed = await refreshAccessToken();
  if (refreshed) {
    localStorage.setItem('token', refreshed);
    return refreshed;
  }
  return token;
}

  // ── Handlers réseau — stables pour removeEventListener ────────────────────
  const handleOnline = useCallback(async () => {
    setIsOffline(false);
    const currentToken = localStorage.getItem("token");
    if (currentToken && isTokenExpiringSoon(currentToken)) {
      await refreshAccessToken();
    }
  }, [isTokenExpiringSoon, refreshAccessToken]);

  const handleOffline = useCallback(() => setIsOffline(true), []);

  // ── Firebase Messaging ─────────────────────────────────────────────────────
  const initFirebaseMessaging = useCallback(async (currentUser) => {
    const messaging = window?.cordova?.plugins?.firebase?.messaging;
    if (!messaging) return;

    try {
      await messaging.requestPermission();
      const fcmtoken = await messaging.getToken();

      if (currentUser._id && fcmtoken) {
        try {
          await api.post(`${API_URL}/api/users/${currentUser._id}/fcm-token`, { fcmtoken });
          localStorage.setItem('fcmToken', fcmtoken);
        } catch (err) {
          console.error("❌ Envoi token FCM :", err);
        }
      }

      messaging.onTokenRefresh(async (newToken) => {
        if (currentUser._id && newToken) {
          try {
            await api.post(`${API_URL}/api/users/${currentUser._id}/fcm-token`, { fcmtoken: newToken });
            localStorage.setItem('fcmToken', newToken);
          } catch (err) {
            console.error("❌ Envoi nouveau token FCM :", err);
          }
        }
      });
    } catch (error) {
      console.error("❌ Firebase Messaging :", error);
    }
  }, []);

  // ── Video call ─────────────────────────────────────────────────────────────
  const startVideoCall = useCallback(async (discussionId) => {
    const storedUser = localStorage.getItem('user');
    const userObj    = JSON.parse(storedUser);

    try {
      const response = await api.get(
        `${API_URL}/api/discussions/${discussionId}/participants`
      );
      const participants = response.data.participants;

      socketRef.current?.emit('start-group-call', {
        discussionId,
        participants: participants.map(p => p._id),
        caller: { userId: userObj.id },
      });

      setCallInProgress(true);
      navigate('/VideoCall', { state: { participants, discussionId, isCaller: true } });
    } catch (error) {
      console.error("❌ Démarrage appel vidéo :", error);
    }
  }, [navigate]);

  // ── Initialisation principale ──────────────────────────────────────────────
//useSessionReset(socket, !!user);
useEffect(() => {
  if (!user?.email || !user?._id) return;

  const newSocket = io(SOCKET_URL, {
    transports: ['websocket'],
    withCredentials: true,
    // Appelée à CHAQUE tentative de connexion ; peut être asynchrone
    auth: async (cb) => cb({ token: await getFreshToken() }),
  });

  socketRef.current = newSocket;

  let authRetries = 0;
  let hasConnectedOnce = false;
  let needsCatchUp = false;
  let hiddenAt = null;

  newSocket.on('connect', () => {
    authRetries = 0;
    newSocket.emit('register', {
      profileImage: user.profileImage,
      profileImageUpdatedAt: user.profileImageUpdatedAt,
    });

    // Après une coupure, les messages émis pendant l'absence ne sont jamais rejoués par le serveur :
    // on demande aux composants de recharger ce qu'ils affichent.
    if (hasConnectedOnce || needsCatchUp) window.dispatchEvent(new Event('socket-reconnected'));
    hasConnectedOnce = true;
    needsCatchUp = false;
  });

  newSocket.on('disconnect', (reason) => console.warn('❌ Socket déconnecté :', reason));

  
  newSocket.on('connect_error', async (err) => {
    console.error('⚠️ Socket erreur :', err.message);
    needsCatchUp = true;

    if (!/token|authentification/i.test(err.message)) return;   
    if (authRetries >= 3) return;                                
    authRetries += 1;

    const newToken = await refreshAccessToken();
    if (newToken) {
      localStorage.setItem('token', newToken);
      newSocket.connect();      
    }
  });

  // Application mise en veille : la connexion peut être morte sans que le client le sache
  const markHidden = () => { hiddenAt = Date.now(); };
  const onResume = () => {
    const away = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = null;
    if (!newSocket.connected) { newSocket.connect(); return; }
    if (away > 30000) newSocket.disconnect().connect();          // connexion neuve, donc nouveau `register` et rattrapage
  };
  const onVisibility = () => (document.visibilityState === 'hidden' ? markHidden() : onResume());

  document.addEventListener('pause', markHidden);                 // Cordova
  document.addEventListener('resume', onResume);                  // Cordova
  document.addEventListener('visibilitychange', onVisibility);    // navigateur

  setSocket(newSocket);

  return () => {
    document.removeEventListener('pause', markHidden);
    document.removeEventListener('resume', onResume);
    document.removeEventListener('visibilitychange', onVisibility);
    newSocket.disconnect();
  };
}, [user?._id]);
 


 // Valeurs récentes accessibles sans relancer l'effet
const depsRef = useRef({});
depsRef.current = {
  navigate, isTokenExpiringSoon, refreshAccessToken,
  handleOnline, handleOffline, initFirebaseMessaging,
};

const isTokenExpired = (t) => {
  try {
    const b64 = t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64)).exp * 1000 < Date.now();
  } catch { return true; }
};

useEffect(() => {
  let cancelled = false;
  const onOnline  = (e) => depsRef.current.handleOnline?.(e);
  const onOffline = (e) => depsRef.current.handleOffline?.(e);

  const boot = async () => {

    try {
      const token = localStorage.getItem('token');

      const storedUser = localStorage.getItem('user');
      if (!token || !storedUser) return;

      let parsed;
      try { parsed = JSON.parse(storedUser); }
      catch { localStorage.clear(); depsRef.current.navigate('/connexion'); return; }
      const userWithId = { ...parsed, _id: parsed._id || parsed.id };

      const online = navigator.onLine;
      setIsOffline(!online);

      // 1) Seul cas bloquant : token réellement expiré
      let validToken = token;
      if (online && isTokenExpired(token)) {
        try {
          validToken = await depsRef.current.refreshAccessToken();
        } catch { validToken = null; }
        if (!validToken) { depsRef.current.navigate('/connexion'); return; }
      }
      if (cancelled) return;
      setAuthToken(validToken);

      // 2) Identité E2EE locale (IndexedDB, rapide)
      if (!(await hasLocalIdentity())) {

        console.warn('⚠️ Identité E2EE absente → connexion');
        depsRef.current.navigate('/connexion');
        return;
      }
      if (cancelled) return;

      // 3) Affichage immédiat
      setAuthState({ isAuthenticated: true, user: userWithId });
      setUser(userWithId);

      setUserObj(userWithId);

      window.addEventListener('online', onOnline);
      window.addEventListener('offline', onOffline);

      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = setInterval(async () => {
        if (!navigator.onLine) { setIsOffline(true); return; }
        setIsOffline(false);
        const t = localStorage.getItem('token');
        if (t && depsRef.current.isTokenExpiringSoon(t)) {
          await depsRef.current.refreshAccessToken();
        }
      }, 5 * 60 * 1000);

      // 4) Tout le reste en arrière-plan, sans jamais déconnecter
      if (online) {
        (async () => {
          try {
            const t = localStorage.getItem('token');
            if (t && depsRef.current.isTokenExpiringSoon(t)) {
              const fresh = await depsRef.current.refreshAccessToken();
              if (fresh) setAuthToken(fresh);   // si échec 401 : l'intercepteur api gère
            }
          } catch (e) { console.warn('Refresh en arrière-plan :', e?.message); }
        })();

        depsRef.current.initFirebaseMessaging(userWithId);

        api.get(`${API_URL}/api/users/${userWithId._id}/pending-invitations`)
          .then((res) => {
            if (!cancelled) setNotifications(Array.isArray(res.data) ? res.data : []);
          })
          .catch((e) => console.warn('pending-invitations :', e?.message));
      }
    } catch (err) {
      console.error('❌ Erreur boot :', err);
    } finally {
      // Dans TOUS les chemins (connecté, déconnecté, erreur)
      requestAnimationFrame(() => navigator.splashscreen?.hide());
    }
  };

  if (isCordova) {
    document.addEventListener('deviceready', boot, false);
  } else {
    boot();
  }

  return () => {
    cancelled = true;
    if (isCordova) document.removeEventListener('deviceready', boot);
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}, [isCordova]);   // une seule exécution

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div className="messagerie">
      {prompt.modal}

      {isOffline && (
        <div style={{
          position: 'fixed', bottom: '20px', left: '50%',
          transform: 'translateX(-50%)', width: '80%', maxWidth: '400px',
          padding: '10px 16px', backgroundColor: '#f44336', color: 'white',
          textAlign: 'center', borderRadius: '8px',
          boxShadow: '0 4px 10px rgba(0,0,0,0.2)', zIndex: 1000, fontWeight: '500',
        }}>
          📴 {t('discussionlist.offline')}
        </div>
      )}

      <div>
        <div className="menu_haut toolbar">
          {!shouldHideMenu && (
            <MenuComponent
              socket={socket}
              selectedDiscussion={selectedDiscussion}
              setSelectedDiscussion={setSelectedDiscussion}
              startVideoCall={startVideoCall}
              setCallInProgress={setCallInProgress}
              callInProgress={callInProgress}
              onLogout={handleLogout}
              prompt={prompt}
            />
          )}
        </div>

        <div className="content">
          <DiscussionsProvider key={user?._id || 'anon'}>
	<AuthGuard /> 
<Suspense fallback={<div style={{ padding: 20 }}>Chargement…</div>}>
            <Routes>
              <Route path="/verifyemail"  element={<VerifyEmail />} />
    <Route path="/connexion"    element={<Connexion socket={socket} onLogin={setUser} />} />
    <Route path="/onboarding"   element={<Onboarding />} />
    <Route path="/2fa"          element={<TwoFactor socket={socket} onLogin={setUser} prompt={prompt} />} />
    <Route path="/statsElsa@1973" element={<Dashboard />} />

              {/* ── Routes privées ── */}
              {user && (
                <>
                  <Route path="/public" element={
                    <PublicGroups
                      user={user} socket={socket} prompt={prompt}
                      userObj={user} setCallInProgress={setCallInProgress}
                      selectedDiscussion={selectedDiscussion}
                      setSelectedDiscussion={setSelectedDiscussion}
                    />
                  } />
                  <Route path="/profile"       element={<Profile user={user} socket={socket} prompt={prompt} email={user?.email || ""} />} />
                  <Route path="/TwoFactorSetup" element={<TwoFactorSetup />} />
                  <Route path="/ImportContacts" element={<ImportContacts contacts={contacts} setContacts={setContacts} socket={socket} prompt={prompt} email={user?.email || ""} />} />
                  <Route path="/CreateContact"  element={<CreateContact socket={socket} prompt={prompt} email={user?.email || ""} />} />
                  <Route path="/Infos"          element={<Infos socket={socket} email={user?.email || ""} />} />
                  <Route path="/Parametres"     element={<Parametres socket={socket} prompt={prompt} email={user?.email || ""} />} />
<Route
  path="/recovery"
  element={<RecoverySettings socket={socket} prompt={prompt} email={user?.email || ""} />} />}
/>
		<Route path="/ParametresLangue"     element={<ParametresLangue socket={socket} prompt={prompt} email={user?.email || ""} />} />
                  <Route path="/VideoCall"      element={<VideoCall socket={socket} prompt={prompt}  setCallInProgress={setCallInProgress} email={user?.email || ""} />} />
                  <Route path="/profile/:id"    element={<UserProfile socket={socket} prompt={prompt} email={user?.email || ""} />} />
                  <Route path="/UserList"       element={<UserList socket={socket} email={user?.email || ""} />} />
                  <Route path="/Whiteboard"     element={<Whiteboard socket={socket} userId={user?._id} discussionId={selectedDiscussion?._id} prompt={prompt} />} />
                  <Route path="/" element={
                    <DiscussionList
                      selectedDiscussion={selectedDiscussion}
                      setSelectedDiscussion={setSelectedDiscussion}
                      startVideoCall={startVideoCall}
                      setCallInProgress={setCallInProgress}
                      prompt={prompt}
                      socket={socket}
                      contacts={contacts}
                      setContacts={setContacts}
                      email={user?.email || ""}
                      notifications={notifications}
                      setNotifications={setNotifications}
                      userObj={user || {}}
			
                    />
                  } />
                  <Route path="/discussion/:id" element={<Discussion socket={socket} prompt={prompt} email={userObj?.email || ""} userObj={userObj || {}} />} />
                  <Route path="/quiz"           element={<QuizPage socket={socket} prompt={prompt} email={userObj?.email || ""} userObj={userObj || {}} />} />
                  <Route path="/quiz/:theme"    element={<QuizPage socket={socket} prompt={prompt} email={userObj?.email || ""} userObj={userObj || {}} />} />
                  <Route path="*"              element={<Navigate to="/" />} />
                </>
              )}
            </Routes>
</Suspense>
          </DiscussionsProvider>
        </div>

        <div className="bottomtoolbar">
          {!shouldHideMenu && (
            <MenuBasComponent
              socket={socket}
              selectedDiscussion={selectedDiscussion}
              setSelectedDiscussion={setSelectedDiscussion}
              startVideoCall={startVideoCall}
              setCallInProgress={setCallInProgress}
              callInProgress={callInProgress}
              onLogout={handleLogout}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default App;